<template>
  <div :class="cn('flex flex-wrap items-center gap-2 font-ds', rootClass)">
    <DSFieldLabel
      v-if="label"
      :label
      class="mr-1"
    />

    <DSBadge
      v-for="tag in selected"
      :key="tagLabel(tag)"
      tone="neutral"
      class="rounded-full"
      :class="removable && !readonly && 'pr-1'"
    >
      {{ tagLabel(tag) }}
      <button
        v-if="removable && !readonly"
        type="button"
        class="inline-flex items-center justify-center size-4 shrink-0 cursor-pointer rounded-full
          text-secondary-foreground/60
          transition-colors duration-ds-fast ease-ds-standard
          hover:bg-foreground/10 hover:text-secondary-foreground
          focus-visible:outline-none focus-visible:shadow-ds-focus"
        :aria-label="`Remove ${tagLabel(tag)}`"
        @click="emit('remove', tag)"
      >
        <X class="size-3" />
      </button>
    </DSBadge>

    <!--
      The ask, drawn as a chip since nothing here is requested until the user
      taps it — the tag assistant no longer asks on its own. Sits where the
      thinking chip and the suggestions below it will land once it has.
    -->
    <button
      v-if="askable && !readonly"
      type="button"
      :class="[
        CHIP,
        `cursor-pointer text-accent-foreground bg-accent border border-accent-foreground/20
          hover:bg-accent-foreground/10
          focus-visible:outline-none focus-visible:shadow-ds-focus`,
      ]"
      @click="emit('ask')"
    >
      <Sparkles class="size-3" />
      Ask AI
    </button>

    <!--
      The assistant, still deciding. Drawn as a chip of its own rather than as
      an overlay on the row: the applied tags stay readable and clickable while
      it thinks, and the chip sits exactly where its answers will appear.
    -->
    <DSThinking
      v-if="loading && !readonly"
      label="Looking for tags…"
      icon-class="size-3"
      :class="[CHIP, 'text-accent-foreground bg-accent border border-accent-foreground/20']"
    />

    <!--
      A suggestion is not a selection. It carries the sparkle and a plus, so one
      tap accepts it — until then it is visibly not yet a tag on this asset.
      Read-only drops the plus with the interaction: a chip that looks like an
      offer and does nothing is worse than a chip that only informs.
    -->
    <component
      :is="readonly ? 'span' : 'button'"
      v-for="tag in suggestions"
      :key="tagLabel(tag)"
      :type="readonly ? undefined : 'button'"
      :class="[
        CHIP,
        'text-accent-foreground bg-accent border border-accent-foreground/20',
        !readonly && `cursor-pointer hover:bg-accent-foreground/10
          focus-visible:outline-none focus-visible:shadow-ds-focus`,
      ]"
      @click="handleAccept(tag)"
    >
      <Sparkles class="size-3" />
      {{ tagLabel(tag) }}
      <Plus
        v-if="!readonly"
        class="size-3"
      />
    </component>

    <button
      v-if="!readonly && addLabel"
      type="button"
      :class="[
        CHIP,
        `cursor-pointer text-muted-foreground border border-dashed border-input
          hover:text-foreground hover:border-strong
          focus-visible:outline-none focus-visible:shadow-ds-focus`,
      ]"
      @click="emit('add')"
    >
      <Plus class="size-3" />
      {{ addLabel }}
    </button>

    <span
      v-if="note"
      class="ml-0.5 text-xs font-medium text-muted-foreground"
    >
      {{ note }}
    </span>
  </div>
</template>

<script setup lang="ts">
import type { HTMLAttributes } from 'vue'
import { Plus, Sparkles, X } from '@lucide/vue'

import { DSBadge, DSFieldLabel, DSThinking } from '@ds'
import { cn } from '@/helpers/cn'

import { TagRef } from '../utils/types'
import { tagLabel } from '../utils/helpers'

/*
 * An asset's tags as a row of chips: what is applied, what is merely suggested,
 * and a way to reach the picker.
 *
 * Presentational, like TagPicker — it holds no tags of its own and decides
 * nothing about them. It speaks TagRef rather than pre-formatted strings so it
 * shares a shape with TagPicker and TagPickerDialog, and so the display name
 * comes from `tagLabel` here rather than from every caller.
 *
 * It carries no outer margin: the gap to whatever sits above belongs to the
 * screen laying it out, so pass `class="mt-4"` rather than assuming one here.
 */
const {
  label = 'Tags',
  addLabel = 'Add tag',
  readonly,
  class: rootClass,
} = defineProps<{
  /** Applied to the asset. Rendered as solid chips. */
  selected: TagRef[]
  /**
   * Proposed but not applied — an assistant's suggestions, typically. Callers
   * filter out anything already in `selected`; a `TagSuggestion` fits here, but
   * its `reason` is not shown at this size (TagPicker is where reasons belong).
   */
  suggestions?: TagRef[]
  /** Caption before the chips. Pass an empty string for a row with no caption. */
  label?: string
  /** An aside after the chips, e.g. what the assistant based the tags on. */
  note?: string
  /** The dashed chip that reaches the picker. Empty string omits it. */
  addLabel?: string
  /** The tag assistant has been asked and has not answered yet. */
  loading?: boolean
  /** Offers the "Ask AI" chip — hidden once the assistant has been asked. */
  askable?: boolean
  /** Chips only: no picker, no accepting, no removing. */
  readonly?: boolean
  /** Puts an x on each applied chip. Off by default — most callers edit in the picker. */
  removable?: boolean
  class?: HTMLAttributes['class']
}>()

const emit = defineEmits<{
  /** A suggestion the user accepted. Applying it is the caller's to do. */
  (e: 'accept', tag: TagRef): void
  (e: 'remove', tag: TagRef): void
  /** The picker was asked for. */
  (e: 'add'): void
  /** The "Ask AI" chip was tapped. */
  (e: 'ask'): void
}>()

/*
 * Read-only still renders suggestions — knowing what was proposed is worth
 * showing — it just does not accept them.
 */
const handleAccept = (tag: TagRef) => {
  if (readonly) return
  emit('accept', tag)
}

/** Both chips are pills on the same metrics; only the fill and the edge differ. */
const CHIP = `inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-bold rounded-full
  transition-colors duration-ds-fast ease-ds-standard`
</script>
